import { useMemo, type FC } from 'react'
import { useFormContext } from 'react-hook-form'
import { Col, Container, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../../../../common/integrations'
import { Button } from '../../../../../../../components/Button/Button'
import { OrderableMultiSelectDropdown } from '../../../../../../../components/OrderableMultiSelectDropdown/OrderableMultiSelectDropdown'
import { type SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetDescribeQuery } from '../../../../../../../store/integrations/integrations'
import { type FormFieldDefinition } from '../../../hooks'
import { Direction, MAX_ORDER_BY_FIELDS, type IntegrationSettings } from '../../../schema'
import styles from './ContactSortBySection.module.scss'

type SortByEntry = { field: string; direction: 'ASC' | 'DESC' }

interface ContactSortBySectionProps {
  serviceId: IntegrationServiceId
}

const uniqueByField = (entries: SortByEntry[]): SortByEntry[] =>
  entries.filter((entry, index) => entries.findIndex((other) => other.field.toLowerCase() === entry.field.toLowerCase()) === index)

export const ContactSortBySection: FC<ContactSortBySectionProps> = ({ serviceId }) => {
  const {
    watch,
    setValue,
    unregister,
    formState: { defaultValues },
  } = useFormContext<IntegrationSettings>()
  const objectName = watch('mappings.contact.objectName')
  const sortBy = watch('mappings.contact.orderBy')
  const currentSortBy: SortByEntry[] = useMemo(() => uniqueByField(sortBy ?? []), [sortBy])
  const integrationName = getIntegrationDisplayName(serviceId)

  const accountId = useAppSelector(selectAccountId)
  const {
    data: describe,
    isFetching: isDescribeFetching,
    isSuccess: isDescribeSuccess,
    isError: isDescribeError,
  } = useGetDescribeQuery({ accountId, connector: serviceId, context: { objectName: objectName! } }, { skip: !objectName || !accountId })
  const fieldOptions = useMemo(
    (): SelectOption[] =>
      (describe?.data?.describe?.formFieldDefinitions || []).map((f: FormFieldDefinition) => ({ id: f.name, label: `${f.label} (${f.name})` })),
    [describe],
  )

  const updateSortBy = (next: SortByEntry[]) => {
    const savedContact = defaultValues?.mappings?.contact
    const savedSortBy = savedContact?.orderBy
    if (next.length === 0 && savedContact && !('orderBy' in savedContact)) {
      unregister('mappings.contact.orderBy', { keepDefaultValue: true, keepDirty: true })
      return
    }
    const emptySortBy = savedSortBy == null ? savedSortBy : null
    setValue('mappings.contact.orderBy', next.length > 0 ? next : emptySortBy, { shouldDirty: true, shouldValidate: true })
  }

  const handleFieldsChange = (fields: string[]) => {
    updateSortBy(fields.map((field) => currentSortBy.find((entry) => entry.field === field) ?? { field, direction: Direction.enum.ASC }))
  }

  const toggleDirection = (field: string) => {
    updateSortBy(
      currentSortBy.map((entry) =>
        entry.field === field ? { ...entry, direction: entry.direction === Direction.enum.DESC ? Direction.enum.ASC : Direction.enum.DESC } : entry,
      ),
    )
  }

  return (
    <Container className='p-0'>
      <Row className='mb-3'>
        <Col>
          <h4 className='mb-2'>Contact Sort Order</h4>
          <p className='zocks-text-gray-600'>
            Choose the {integrationName} fields used to order contacts when Zocks searches them. You can pick up to {MAX_ORDER_BY_FIELDS} fields, and the first
            one takes precedence; drag or use the arrows to reorder. Without a selection, contacts are ordered by the fields mapped to Name. If Name isn&apos;t
            mapped, they&apos;re ordered by the fields mapped to Email, and if neither is mapped, by a default {integrationName} field.
          </p>
        </Col>
      </Row>
      <Row>
        <Col>
          <OrderableMultiSelectDropdown
            options={fieldOptions}
            value={currentSortBy.map((entry) => entry.field)}
            onChange={handleFieldsChange}
            ariaLabel='Add a contact sort field'
            placeholder='Add a field to sort by'
            loading={isDescribeFetching}
            optionsLoaded={isDescribeSuccess}
            disabled={!objectName}
            removeItemLabel='Remove sort field'
            maxItems={MAX_ORDER_BY_FIELDS}
            maxItemsPlaceholder={`Maximum of ${MAX_ORDER_BY_FIELDS} sort fields selected`}
            unknownItemMessage={`Field not found in ${integrationName}`}
            renderItemActions={(field, _index, label) => {
              const descending = currentSortBy.find((entry) => entry.field === field)?.direction === Direction.enum.DESC
              const directionLabel = descending ? 'DSC' : 'ASC'
              return (
                <Button
                  type='button'
                  variant='grayGhost'
                  className={classNames(
                    'd-inline-flex align-items-center justify-content-center flex-shrink-0 py-0 px-2 paragraph-small text-nowrap',
                    styles.directionButton,
                  )}
                  onClick={() => toggleDirection(field)}
                  aria-label={`${label} sort direction: ${directionLabel}`}
                  aria-pressed={descending}
                >
                  <i className={classNames('me-1 bi', descending ? 'bi-sort-down' : 'bi-sort-up-alt')} aria-hidden={true} />
                  {directionLabel}
                </Button>
              )
            }}
          />
          {isDescribeError ? <p className='paragraph-small zocks-text-danger mt-2 mb-0'>Couldn&apos;t load fields from {integrationName}.</p> : null}
        </Col>
      </Row>
    </Container>
  )
}
